// 📍 GPS 근태 등록 클라이언트 (2026-11-05) — 서버 routes/attendance.ts 짝.
//   위치는 누르는 순간 1회만 잰다(상시 추적 없음). 고정밀 GPS 로 몇 초 동안 받아 가장 정확한 값을 쓴다.
import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';

export type AttLang = 'ko' | 'en' | 'vi' | 'zh' | 'es';
export interface AttCode { code: string; names: Record<AttLang, string>; order: number; enabled: boolean }
export interface AttSite { id: string; name: string; lat: number; lng: number; radius: number }
export interface AttConfig { enabled: boolean; visible: boolean; consent: boolean; codes: AttCode[] }
export interface AttTarget { all?: boolean; scopes?: string[]; users?: string[]; exclude?: string[] }
export interface AttSettings { enabled: boolean; target: AttTarget; geocode: boolean; codes: AttCode[]; sites: AttSite[]; updatedAt?: string; updatedBy?: string }
export interface AttRecord {
	_id: string; userId?: string; username?: string; name?: string; code: string; codeName: string; at: string;
	lat: number; lng: number; acc: number; siteId: string | null; siteName: string | null; inSite: boolean; distM: number | null;
	placeAuto: string; place: string; placeEdited: boolean; note: string; proxy?: { status: string };
}
export interface AttResolved { place: string; inSite: boolean; siteId: string | null; siteName: string | null; distM: number | null; hasSites: boolean }
export interface AttFix { lat: number; lng: number; acc: number }

const hdr = (): Record<string, string> => {
	const { authToken, userId } = useAuthStore.getState();
	return { 'Content-Type': 'application/json', ...(authToken && userId ? { 'X-Auth-Token': authToken, 'X-User-Id': userId } : {}) };
};
async function call<T>(path: string, init?: RequestInit): Promise<T> {
	const r = await fetch(`/api/tpt/attendance${path}`, { ...init, headers: hdr() });
	const j = await r.json().catch(() => ({})) as T & { error?: string };
	if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
	return j;
}

export const fetchAttConfig = () => call<AttConfig>('');
export const fetchMyAttendance = () => call<{ records: AttRecord[] }>('?mine=1').then((j) => j.records);
export const giveAttConsent = () => call<{ ok: true }>('?consent=1', { method: 'POST', body: '{}' });
export const resolveAttPlace = (fix: AttFix, lang: string) => call<AttResolved>('?resolve=1', { method: 'POST', body: JSON.stringify({ ...fix, lang }) });
export const submitAttendance = (p: AttFix & { code: string; place: string; note: string; lang: string }) =>
	call<{ ok: true; record: AttRecord }>('', { method: 'POST', body: JSON.stringify(p) }).then((j) => j.record);
export const fetchAttAdmin = (from: string, to: string, q: string) =>
	call<{ settings: AttSettings; records: AttRecord[] }>(`?admin=1&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&q=${encodeURIComponent(q)}`);
export const saveAttSettings = (patch: Partial<Pick<AttSettings, 'enabled' | 'target' | 'geocode' | 'codes' | 'sites'>>) =>
	call<{ ok: true; settings: AttSettings }>('?settings=1', { method: 'PUT', body: JSON.stringify(patch) }).then((j) => j.settings);

/** 코드 이름 — 내 언어, 비어 있으면 한국어 */
export const attCodeName = (c: Pick<AttCode, 'names'> | undefined, lang: string): string => {
	if (!c) return '';
	const l = (lang || 'ko').slice(0, 2) as AttLang;
	return c.names[l] || c.names.ko || '';
};

/** 위치 측정 오류 — 화면 문구 키로 매핑 */
export type FixError = 'unsupported' | 'denied' | 'unavailable' | 'timeout';

/** 📡 고정밀 위치 1회 — watchPosition 으로 최대 maxMs 동안 받아 정확도가 goodM 이하가 되면 바로, 아니면 가장 좋은 값.
 *  onProgress 로 현재 정확도를 알려 준다(화면 "±Nm 측정 중"). 한 번도 못 받으면 FixError 로 reject */
export function getBestFix(onProgress?: (acc: number) => void, maxMs = 15_000, goodM = 20): Promise<AttFix> {
	return new Promise((ok, bad) => {
		if (typeof navigator === 'undefined' || !navigator.geolocation) { bad(new Error('unsupported' satisfies FixError)); return; }
		let best: AttFix | null = null;
		let done = false;
		let watchId = -1;
		const finish = (err?: FixError) => {
			if (done) return;
			done = true;
			window.clearTimeout(timer);
			if (watchId >= 0) navigator.geolocation.clearWatch(watchId);
			if (best) ok(best); else bad(new Error(err || 'timeout'));
		};
		const timer = window.setTimeout(() => finish('timeout'), maxMs);
		watchId = navigator.geolocation.watchPosition((p) => {
			const fix = { lat: p.coords.latitude, lng: p.coords.longitude, acc: Math.max(0.1, p.coords.accuracy || 0) };
			if (!best || fix.acc < best.acc) best = fix;
			onProgress?.(best.acc);
			if (best.acc <= goodM) finish();
		}, (e) => {
			// 권한 거부는 바로 끝, 그 외(일시 실패)는 시간 안에 다시 받기를 기다린다
			if (e.code === e.PERMISSION_DENIED) finish('denied');
			else if (e.code === e.POSITION_UNAVAILABLE && !best) { /* 계속 대기 */ }
		}, { enableHighAccuracy: true, maximumAge: 0, timeout: maxMs });
	});
}

// ── 메뉴 노출용 상태(로그인 후 1회, 설정 저장 시 갱신) ──
let cache: AttConfig | null = null;
let cacheFor = '';
const listeners = new Set<() => void>();
export async function syncAttConfig(force = false): Promise<AttConfig | null> {
	const { userId } = useAuthStore.getState();
	if (!userId) { cache = null; return null; }
	if (!force && cache && cacheFor === userId) return cache;
	try { cache = await fetchAttConfig(); cacheFor = userId; } catch { cache = null; }
	listeners.forEach((fn) => fn());
	return cache;
}
export function useAttConfig(): AttConfig | null {
	const [v, setV] = useState<AttConfig | null>(cache);
	useEffect(() => {
		const fn = () => setV(cache);
		listeners.add(fn);
		void syncAttConfig();
		return () => { listeners.delete(fn); };
	}, []);
	return v;
}

export const ATTENDANCE_OPEN_EVENT = 'tpt-attendance-open';
